import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navItems } from "@/mocks/home";
import { googleFormUrl } from "@/mocks/contact";
import FlightProgress from "@/components/feature/FlightProgress";

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-[72px] w-full max-w-content items-stretch justify-between px-5 md:px-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 whitespace-nowrap"
          onClick={() => setMenuOpen(false)}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-plane-line text-[18px]" />
          </span>
          <span className="font-heading text-[16px] font-bold tracking-tight text-foreground-950 md:text-[17px]">
            ゼロからアメリカ留学
          </span>
        </Link>

        <nav className="hidden items-stretch md:flex" aria-label="メインメニュー">
          <ul className="flex items-stretch gap-3.5 lg:gap-6">
            {navItems.map((item) => {
              const active = isActive(item.to);

              if (item.external && googleFormUrl) {
                return (
                  <li key={item.to} className="relative flex items-stretch">
                    <a
                      href={googleFormUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex items-center gap-1 py-1 whitespace-nowrap text-[12.5px] font-medium text-foreground-600 transition-colors duration-200 hover:text-primary-700 lg:text-[14px]"
                    >
                      {item.label}
                      <i className="ri-external-link-line text-[11px] text-foreground-400 transition-colors duration-200 group-hover:text-primary-600" />
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-0 bottom-0 h-[2px] origin-left scale-x-0 rounded-full bg-primary-500 transition-transform duration-200 group-hover:scale-x-100"
                      />
                    </a>
                  </li>
                );
              }

              return (
                <li key={item.to} className="relative flex items-stretch">
                  <Link
                    to={item.to}
                    aria-current={active ? "page" : undefined}
                    className={`group relative flex items-center py-1 whitespace-nowrap text-[12.5px] transition-colors duration-200 lg:text-[14px] ${
                      active
                        ? "font-semibold text-foreground-950"
                        : "font-medium text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {item.label}
                    <span
                      aria-hidden="true"
                      className={`absolute inset-x-0 bottom-0 h-[2px] origin-left rounded-full bg-primary-500 transition-transform duration-200 ${
                        active ? "scale-x-100" : "scale-x-0"
                      } group-hover:scale-x-100`}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <button
          type="button"
          aria-label="メニューを開く"
          className="flex h-full w-10 items-center justify-center text-foreground-900 md:hidden"
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </div>

      <FlightProgress />

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <ul className="mx-auto flex w-full max-w-content flex-col px-5 py-2">
            {navItems.map((item) => {
              const active = isActive(item.to);

              if (item.external && googleFormUrl) {
                return (
                  <li key={item.to}>
                    <a
                      href={googleFormUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="flex min-h-[48px] items-center justify-between border-b border-background-200/70 py-3.5 text-[15px] text-foreground-700 transition-colors duration-200 last:border-b-0 active:text-primary-700"
                    >
                      <span className="flex items-center gap-2">
                        {item.label}
                        {item.free && (
                          <span className="rounded-full bg-primary-100 px-1.5 py-[1px] text-[9.5px] font-semibold text-primary-800">
                            無料
                          </span>
                        )}
                      </span>
                      <i className="ri-arrow-right-up-line text-lg text-foreground-400" />
                    </a>
                  </li>
                );
              }

              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    aria-current={active ? "page" : undefined}
                    className={`flex min-h-[48px] items-center justify-between border-b border-background-200/70 py-3.5 text-[15px] transition-colors duration-200 last:border-b-0 ${
                      active
                        ? "font-bold text-primary-700"
                        : "text-foreground-700 active:text-primary-700"
                    }`}
                  >
                    {item.label}
                    <i
                      className={`text-lg ${active ? "ri-check-line text-primary-600" : "ri-arrow-right-s-line text-foreground-400"}`}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </header>
  );
}